import type { DataEntityDto, DataModelState, DataTypeDto } from '@bk/contracts';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Card, PageTitle, SectionLoader } from '../../ui';
import { useSections } from './SectionQuestions';

/** Testo confrontabile: minuscolo, senza accenti e senza «_». */
const fold = (v: unknown) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();

/**
 * I soggetti per ruolo (ADR 0193 § 7-bis.1, 0202): un solo tipo soggetto, i ruoli (cliente, fornitore, inquilino…) nelle
 * voci del ruolo del soggetto, legate al soggetto per nome.
 */
export function rolesOf(types: DataTypeDto[]) {
  const subject = types.find((t) => t.voce_strato === 's2.ent.soggetto');
  const role = types.find((t) => t.voce_strato === 's2.ent.ruolo_soggetto');
  const subjects = (ruolo: string): DataEntityDto[] => {
    if (!subject || !role) return [];
    const names = new Set(role.entita.filter((r) => fold(r.attributi.ruolo) === fold(ruolo)).map((r) => fold(r.attributi.di ?? r.nome)));
    return subject.entita.filter((e) => names.has(fold(e.nome)));
  };
  return { subject, subjects, count: (ruolo: string) => subjects(ruolo).length };
}

/** La vista di un ruolo nel menu (Clienti, Fornitori, Inquilini…): i soggetti con quel ruolo, con la loro scheda. */
export function RoleViewPage({ ruolo }: { ruolo: string }) {
  const { tenant } = useTenant();
  const q = useTenantQuery<DataModelState>(['data-model'], '/data-model');
  const sections = useSections();
  if (q.isLoading) return <SectionLoader />;
  const roles = rolesOf(q.data?.data ?? []);
  const list = roles.subjects(ruolo);
  const name = sections.state?.aree.flatMap((a) => a.viste).find((v) => v.ruolo === ruolo)?.nome ?? ruolo;
  return (
    <div className="space-y-4">
      <PageTitle title={name} subtitle={`I soggetti con il ruolo «${ruolo.replace(/_/g, ' ')}»: una persona o un’impresa sola, con tutti i suoi ruoli.`} />
      <Card className="p-0">
        {list.length ? (
          <ul className="divide-y divide-black/[0.05]">
            {list.map((e) => (
              <li key={e.rif}>
                <a href={`#/t/${tenant.id}/e/${roles.subject!.chiave}/${encodeURIComponent(e.rif)}`} className="block px-5 py-3 text-sm text-ink hover:bg-accent/[0.04]">
                  {e.nome}
                  {e.attributi.partita_iva ? <span className="ml-2 text-xs text-ink/50">P.IVA {e.attributi.partita_iva}</span> : null}
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-4 text-sm text-ink/55">Ancora nessuno: i ruoli arrivano dalle fatture, dai contratti e dai programmi collegati.</p>
        )}
      </Card>
    </div>
  );
}
